import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import DeleteConfirmationModal from './projects/DeleteConfirmationModal';
import ProjectCard from './projects/ProjectCard';
import ProjectSettingsModal from './projects/ProjectSettingsModal';
import ProjectRefreshIframe from './projects/ProjectRefreshIframe';
import { fetchProjects, fetchDocuments, createOrUpdateProject, updateProject, deleteProject, refreshProject } from '../api/projectsApi';

const API_URL = process.env.REACT_APP_API_URL;

// Document type with fileName and documentId
interface DocumentItem {
  fileName: string;
  documentId: string;
}

const Projects: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<DocumentItem | null>(null);
  const [showProjectSettings, setShowProjectSettings] = useState(false);
  const [category, setCategory] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [numberOfStories, setNumberOfStories] = useState('');
  const [completed, setCompleted] = useState(''); 
  const [projects, setProjects] = useState<any[]>([]);
  const [refreshResult, setRefreshResult] = useState<any | null>(null);
  const { user } = useAuth(); // Get user from session

  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<any | null>(null);

  useEffect(() => {
    const fetchAndSetProjects = async () => {
      if (!user?.id) return;
      try {
        const data = await fetchProjects(user.id);
        if (data && Array.isArray(data.projects)) {
          setProjects(data.projects);
        } else {
          setProjects([]);
        }
      } catch (err) {
        setProjects([]);
      }
    };
    fetchAndSetProjects();
  }, [user]);

  useEffect(() => {
    const handler = (e: any) => {
      if (e.detail && Array.isArray(e.detail)) {
        setProjects(e.detail);
      }
    };
    window.addEventListener('refreshProjects', handler);
    return () => window.removeEventListener('refreshProjects', handler);
  }, []);

  const handleGetStarted = async () => {
    setLoading(true);
    setError(null);
    setDocuments([]);
    setSelectedDocument(null);
    setShowProjectSettings(false);
    try {
      const data = await fetchDocuments(user?.id || '');
      if (data && Array.isArray(data.data)) {
        setDocuments(data.data);
        setShowModal(true);
      } else {
        setError('No document list found.');
      }
    } catch (err) {
      setError('Failed to fetch documents.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    setError(null);
    setDocuments([]);
    setSelectedDocument(null);
    setShowProjectSettings(false);
  };

  const handleSelectDocument = (doc: DocumentItem) => {
    setSelectedDocument(doc);
    // Reset all fields for a new project
    setCategory('');
    setDueDate('');
    setNumberOfStories('');
    setCompleted('');
    setShowModal(false);
    setShowProjectSettings(true);
  };

  const handleProjectSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDocument) return;
    const newProject = {
      userId: user?.id,
      fileName: selectedDocument.fileName,
      documentId: selectedDocument.documentId,
      category,
      dueDate,
      numberOfStories,
      completedStories: completed ? Number(completed) : 0,
      progress: numberOfStories && completed ? Math.round((Number(completed) / Number(numberOfStories)) * 100) : 0,
      total: numberOfStories ? Number(numberOfStories) : 0
    };
    try {
      await createOrUpdateProject(newProject);
      if (user?.id) {
        const data = await fetchProjects(user.id);
        if (data && Array.isArray(data.projects)) {
          setProjects(data.projects);
        }
      }
    } catch (err) {}
    setShowProjectSettings(false);
    setCategory('');
    setDueDate('');
    setNumberOfStories('');
    setCompleted(''); 
    setSelectedDocument(null);
  };

  const handleUpdateProject = async (projectId: string, updatedProject: any) => {
    try {
      await updateProject(projectId, updatedProject);
      const data = await fetchProjects(user?.id || '');
      if (data && Array.isArray(data.projects)) {
        setProjects(data.projects);
      }
    } catch (err) {}
  };

  // Open custom delete confirmation modal
  const handleDeleteProject = (project: any) => {
    setProjectToDelete(project);
    setShowDeleteModal(true);
  };

  // Confirm deletion
  const confirmDeleteProject = async () => {
    if (!user?.id || !projectToDelete?._id) {
      setShowDeleteModal(false);
      setProjectToDelete(null);
      return;
    }
    try {
      await deleteProject(projectToDelete._id);
      const data = await fetchProjects(user.id);
      if (data && Array.isArray(data.projects)) {
        setProjects(data.projects);
      }
    } catch (err) {}
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  // Cancel deletion
  const cancelDeleteProject = () => {
    setShowDeleteModal(false);
    setProjectToDelete(null);
  };

  const handleEditProject = (project: any) => {
    setSelectedDocument({ fileName: project.fileName, documentId: project.documentId });
    // Format due date as YYYY-MM-DD for input type="date"
    let formattedDueDate = '';
    if (project.dueDate) {
      const dateObj = new Date(project.dueDate);
      if (!isNaN(dateObj.getTime())) {
        formattedDueDate = dateObj.toISOString().slice(0, 10);
      }
    }
    setCategory(project.category || '');
    setDueDate(formattedDueDate);
    setNumberOfStories(project.numberOfStories || '');
    setCompleted(project.completedStories?.toString() || '');
    setShowProjectSettings(true);
  };

  const handleRefreshProject = async (project: any) => {
    if (!user?.id || !project?._id) return;
    setRefreshResult({ __loading: true }); // Show loading modal immediately
    const response = await refreshProject(user.id, project._id);
    if (response && response.data) {
      if (project.openAISummary) {
        setRefreshResult({ ...response.data, showSummaryType: true });
      } else {
        setRefreshResult(response.data);
      }
    } else if (response && response.updated === false) {
      setRefreshResult({ summary: 'Document is already up to date!' });
    } else {
      setRefreshResult(null); // Hide modal on error
    }
    const data = await fetchProjects(user.id);
    if (data && Array.isArray(data.projects)) {
      setProjects(data.projects);
    }
  };

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">My Projects</h1>
            <p className="text-gray-300">Manage your writing projects and track your progress</p>
          </div>
          <Button className="bg-custom-blue-600 hover:bg-custom-blue-700 text-white" onClick={handleGetStarted}>
            + New Project
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Render user-created projects */}
          {projects.length === 0 && (
            <Card className="border-dashed border-2 border-parchment-400 bg-paper shadow-paper text-custom-blue-900">
              <CardHeader>
                <CardTitle className="text-custom-blue-900">Create Your First Project</CardTitle>
                <CardDescription className="text-custom-blue-700">Start a new writing project to begin tracking your progress</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">📝</div>
                  <p className="text-custom-blue-700 mb-4">No projects yet</p>
                  <Button variant="outline" className="w-full bg-paper border-parchment-300 text-custom-blue-800 hover:bg-parchment-100" onClick={handleGetStarted} disabled={loading}>
                    {loading ? 'Loading...' : 'Get Started'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {projects.map((project, idx) => (
            <div key={project.documentId || project._id || idx + '-container'}>
              <ProjectCard
                key={project.documentId || project._id || idx}
                project={project}
                onEdit={handleEditProject}
                onDelete={handleDeleteProject}
                onRefresh={handleRefreshProject}
                userId={user?.id || ''}
              />
            </div>
          ))}
        </div>        <div className="mt-12">
          <Card className="bg-paper border-parchment-300 text-custom-blue-900 shadow-paper">
            <CardHeader>
              <CardTitle className="text-custom-blue-900">📊 Project Management Features</CardTitle>
              <CardDescription className="text-custom-blue-700">Coming soon to help you stay organized</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <div>
                  <h4 className="font-semibold mb-2 text-custom-blue-900">✍️ Writing Tools</h4>
                  <ul className="space-y-1 text-custom-blue-700">
                    <li>• Word count tracking</li>
                    <li>• Chapter organization</li>
                    <li>• Writing goals & deadlines</li>
                    <li>• Progress visualization</li>
                  </ul>
                </div>                <div>
                  <h4 className="font-semibold mb-2 text-custom-blue-900">🤝 Collaboration</h4>
                  <ul className="space-y-1 text-custom-blue-700">
                    <li>• Share drafts with beta readers</li>
                    <li>• Feedback and comments</li>
                    <li>• Version control</li>
                    <li>• Co-author permissions</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Modal for document list */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-4 max-w-md w-full relative">
            <button onClick={handleClose} className="absolute top-2 right-2 text-gray-600 hover:text-black">&times;</button>
            <h2 className="text-lg font-bold mb-4">Select a Google Drive Document</h2>
            {error && <div className="text-red-500 mb-2">{error}</div>}
            {loading ? (
              <div>Loading...</div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {documents.map((doc) => (
                  <li key={doc.documentId} className="py-2 cursor-pointer hover:bg-gray-100 px-2 rounded" onClick={() => handleSelectDocument(doc)}>
                    <span className="font-medium">{doc.fileName}</span>
                  </li>
                ))}
                {documents.length === 0 && <li className="text-gray-500">No documents found.</li>}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Project Settings Modal */}
      <ProjectSettingsModal
        show={showProjectSettings && !!selectedDocument}
        selectedDocument={selectedDocument}
        category={category}
        dueDate={dueDate}
        numberOfStories={numberOfStories}
        completed={completed}
        onChange={(field, value) => {
          if (field === 'category') setCategory(value);
          else if (field === 'dueDate') setDueDate(value);
          else if (field === 'numberOfStories') setNumberOfStories(value);
          else if (field === 'completed') setCompleted(value);
        }}
        onSubmit={handleProjectSettingsSubmit}
        onClose={handleClose}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteModal && !!projectToDelete}
        projectName={projectToDelete?.fileName || projectToDelete?.name || 'Untitled Project'}
        onCancel={cancelDeleteProject}
        onConfirm={confirmDeleteProject}
      />

      {/* Optionally, show selected document name for debug/demo */}
      {selectedDocument && !showProjectSettings && (
        <div className="fixed bottom-4 right-4 bg-white border shadow-lg p-4 rounded max-w-md z-40">
          <div className="font-bold mb-2">Selected Document:</div>
          <div className="text-xs whitespace-pre-wrap break-all">{selectedDocument.fileName}</div>
        </div>
      )}

      {/* Project Refresh Iframe - shows API response data if available */}
      {refreshResult && (
        <ProjectRefreshIframe
          data={refreshResult}
          onClose={async () => {
            setRefreshResult(null); // unmount first
            if (user?.id) {
              const data = await fetchProjects(user.id);
              if (data && Array.isArray(data.projects)) {
                setProjects(data.projects);
              }
            }
          }}
          userId={user?.id || ''}
        />
      )}
    </div>
  );
};

export default Projects;
